import type { PhotosConfig, TileData } from "./types";

/**
 * Loads /assets/photos/photos.json and exposes an equal-probability photo picker.
 *
 * The picker follows the rule:
 *   1. Pick a person uniformly at random from the chosen persons.
 *   2. Pick one of that person's photos uniformly at random.
 *
 * This avoids the "photos with more entries appear more often" bias that a
 * naive flat-pick over the photo pool would introduce.
 */
export class AssetConfig {
  private config: PhotosConfig | null = null;
  /** personId -> photo array (preserves photos.json ordering). */
  private photosByPerson: Map<string, TileData[]> = new Map();

  async load(url = "assets/photos/photos.json"): Promise<void> {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to load photo config at ${url}: HTTP ${res.status}`);
    }
    const data = (await res.json()) as PhotosConfig;
    this.setConfig(data);
  }

  /** Inject a config object directly (useful when the JSON is preloaded by another loader). */
  setConfig(data: PhotosConfig): void {
    this.config = data;
    this.photosByPerson.clear();
    for (const photo of data.photos) {
      const bucket = this.photosByPerson.get(photo.personId);
      if (bucket) bucket.push(photo);
      else this.photosByPerson.set(photo.personId, [photo]);
    }
  }

  /** Returns the underlying config (or throws if not yet loaded). */
  getConfig(): PhotosConfig {
    if (!this.config) throw new Error("AssetConfig not loaded — call .load() first.");
    return this.config;
  }

  /** All person ids available in the loaded config. */
  listPersonIds(): string[] {
    if (!this.config) throw new Error("AssetConfig not loaded — call .load() first.");
    return this.config.persons.map((p) => p.personId);
  }

  /**
   * Choose a photo uniformly across the given person pool.
   *
   *   - First pick a person uniformly at random from `personIds`.
   *   - Then pick one of that person's photos uniformly at random.
   *
   * This satisfies the equal-probability requirement even if persons have
   * different numbers of photos.
   */
  pickPhoto(personIds: string[]): TileData {
    if (!this.config) throw new Error("AssetConfig not loaded — call .load() first.");
    if (personIds.length === 0) throw new Error("personIds must not be empty");

    const personId = personIds[Math.floor(Math.random() * personIds.length)];
    const bucket = this.photosByPerson.get(personId);
    if (!bucket || bucket.length === 0) {
      throw new Error(`No photos registered for personId=${personId}`);
    }
    const tile = bucket[Math.floor(Math.random() * bucket.length)];
    return tile;
  }
}
